<template>
    <div
        class="ds-week-cell ds-timesheet-week-cell"
        :class="{ 'cursor-pointer': canEdit }"
        :style="mode === 'dayState' ? { backgroundColor: dayStateColor } : undefined"
        :data-schedule-date="date"
    >
        <div v-if="stripe" class="ds-week-cell-stripe" :class="STRIPE_CLASSES[stripe]" />

        <div v-if="mode === 'work'" class="ds-timesheet-week-body">
            <div class="ds-timesheet-week-top">
                <div class="ds-timesheet-week-lines">
                    <span v-if="mainText" class="ds-timesheet-week-line">
                        <span class="ds-timesheet-week-caption">осн.</span>{{ mainText }}
                    </span>
                    <span v-if="hasAdditional" class="ds-timesheet-week-line">
                        <span class="ds-timesheet-week-caption">доп.</span><span class="ds-timesheet-week-extra">{{ additionalText }}</span>
                    </span>
                    <span v-if="hasNight" class="ds-timesheet-week-line">
                        <span class="ds-timesheet-week-caption">ночных</span>{{ nightText }}
                    </span>
                </div>
                <div v-if="factText" class="ds-timesheet-week-total">
                    <span class="ds-timesheet-week-total-value">{{ factText }}</span>
                    <span class="ds-timesheet-week-caption">итого</span>
                </div>
            </div>
            <div v-if="violationChip" class="ds-week-chips">
                <span class="ds-week-chip ds-week-chip-violation">{{ violationChip }}</span>
            </div>
        </div>

        <div v-else-if="mode === 'absence'" class="ds-timesheet-week-body">
            <span class="ds-week-day-state-title">Прогул</span>
            <span class="ds-timesheet-week-note">{{ shortName }}<template v-if="hasFact"> · учтено {{ factText }}</template></span>
        </div>

        <!-- КС + доп. смена (r2): КС заголовком и фоном, отдельно часы КС и доп., справа готовый итог дня. -->
        <div v-else-if="mode === 'dayState' && dayStateExtra" class="ds-timesheet-week-body">
            <!-- Чип нарушения доп. смены — в строке заголовка, чтобы ячейка оставалась 64 px (review RD-139 r2 #1). -->
            <div class="ds-timesheet-week-top ds-timesheet-week-head">
                <span class="ds-week-day-state-title ds-timesheet-week-name">{{ dayStateName }}</span>
                <div v-if="violationShortChip" class="ds-week-chips">
                    <span class="ds-week-chip ds-week-chip-violation">{{ violationShortChip }}</span>
                </div>
            </div>
            <div class="ds-timesheet-week-top ds-timesheet-week-split">
                <div class="ds-timesheet-week-lines">
                    <span class="ds-timesheet-week-line">
                        <span class="ds-timesheet-week-caption">{{ dayStateShortName }}</span>{{ dayStateHoursText }}
                    </span>
                    <span class="ds-timesheet-week-line">
                        <span class="ds-timesheet-week-caption">доп.</span>
                        <span v-if="dayStateExtra.kind === 'additional'" class="ds-timesheet-week-extra">{{ dayStateExtra.text }}</span>
                        <template v-else-if="dayStateExtra.kind === 'absence'">
                            {{ dayStateExtra.code }}<template v-if="dayStateExtra.text"> · {{ dayStateExtra.text }}</template>
                        </template>
                        <template v-else>{{ dayStateExtra.text }}</template>
                    </span>
                </div>
                <div v-if="hasFact" class="ds-timesheet-week-total">
                    <span class="ds-timesheet-week-total-value">{{ factText }}</span>
                    <span class="ds-timesheet-week-caption">учтено</span>
                </div>
            </div>
        </div>

        <div v-else-if="mode === 'dayState' || mode === 'code'" class="ds-timesheet-week-body">
            <div class="ds-timesheet-week-top">
                <span class="ds-week-day-state-title ds-timesheet-week-name">{{ mode === 'dayState' ? dayStateName : fullName }}</span>
                <div v-if="showCodeHours" class="ds-timesheet-week-total">
                    <span class="ds-timesheet-week-total-value">{{ factText }}</span>
                    <span class="ds-timesheet-week-caption">учтено</span>
                </div>
            </div>
        </div>

        <div v-else-if="mode === 'dayoff'" class="ds-week-cell-label">Выходной</div>

        <div v-if="isToday" class="ds-week-cell-today" aria-hidden="true" />
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { Schedule } from '@/types'
import { useAuthStore } from '@/stores/authStore'
import { useTimesheetCell } from './timesheetCell'

interface Props {
    schedule: Schedule
    date: string
}

// Полные имена классов: Tailwind вырезает из @layer components классы, собранные шаблонной строкой.
const STRIPE_CLASSES = { 'warning': 'ds-week-cell-stripe-warning', 'no-marks': 'ds-week-cell-stripe-no-marks' } as const

const props = defineProps<Props>()

const authStore = useAuthStore()
// Недельная ячейка без подсказки (RD-139): всё нужное показано в самой ячейке.
const {
    isToday, mode, totals, shortName, fullName, dayStateName, dayStateShortName, dayStateColor, dayStateHoursText, dayStateExtra,
    hasAdditional, hasNight, hasFact, factText, mainText, additionalText, nightText, violationChip, violationShortChip, stripe,
} = useTimesheetCell(props)

const canEdit = computed(() => Boolean(authStore.permissions?.can_edit_timesheet))
// Код без часов не получает фиктивный ноль; у кадрового состояния учтённые часы — из ответа.
const showCodeHours = computed(() => hasFact.value && (mode.value === 'dayState' || (totals.value.fact_seconds ?? 0) > 0))
</script>
